/* eslint react/display-name: 0 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import { Segment, Grid, Dimmer, Loader } from 'semantic-ui-react';

import { STATUS } from '../../../../server/constants';

import { post } from '_utils/xhr';
import Notes from '_views/notes'

import UserInfo from './info';
import UserMenu from './menu';
import UserProfile from './profile';
import UserLics from './licenses';
import UserRoles from './roles';
import UserSecurity from './security';

const USER_PANES = {
  ACCOUNT: 'Account',
  LICENSES: 'Licenses',
  NOTES: 'Notes',
  ROLES: 'Roles',
  SECURITY: 'Security'
};

const UserItem = ({
  user,
  onRefreshUser,
  onModifyUser,
  onAddLicense,
  onModifyLicense,
  onReactivateLicense,
  onRemoveLicense,
  onAddSublicense,
  onRemoveSublicense,
  onRemoveLicenseMachine,
  onDeactivateLicenseUpgrade,
  onAddNote,
  onRemoveNote,
  availableRoles,
  isLoading,
  setNavigation,
}) => {
  const [activePane, setActivePane] = useState(USER_PANES.ACCOUNT);

  const handleMenuClick = (e, data) => {
    setActivePane(data.name);
  };

  const handleRefreshUser = () => {
    onRefreshUser({ userId: user._id });
  };

  const handleProfileSubmit = (payload) => {
    return onModifyUser({ userId: user._id, payload });
  };

  const handleAddLicense = (payload) => {
    return onAddLicense({ userId: user._id, payload });
  };

  const handleModifyLicense = ({ licenseId, payload }) => {
    return onModifyLicense({ userId: user._id, licenseId, payload });
  };

  const handleReactivateLicense = (licenseId) => {
    return onReactivateLicense({ userId: user._id, licenseId });
  };

  const handleRemoveLicense = ({ licenseId }) => {
    return onRemoveLicense({ userId: user._id, licenseId });
  };

  const handleAddSublicense = ({ licenseId, payload }) => {
    return onAddSublicense({ userId: user._id, licenseId, payload });
  };

  const handleRemoveSublicense = ({ licenseId, sublicenseId }) => {
    return onRemoveSublicense({ userId: user._id, licenseId, sublicenseId });
  };

  const handleRemoveLicenseMachine = ({ licenseId, machineId }) => {
    return onRemoveLicenseMachine({ userId: user._id, licenseId, machineId });
  };

  const handleDeactivateLicenseUpgrade = ({ licenseId, licenseUpgradeId }) => {
    return onDeactivateLicenseUpgrade({ userId: user._id, licenseId, licenseUpgradeId });
  };

  const handleAddNote = (note) => {
    return onAddNote({ userId: user._id, payload: { note } });
  };

  const handleRemoveNote = (noteId) => {
    return onRemoveNote({ userId: user._id, noteId });
  };

  const handleResendVerification = () => {
    if (window.confirm(`Resend the user email verification for ${user.displayName} at ${user.email}?`)) {
      post(`/accounts/${user._id}/verification`, {})
        .then(() => { window.alert(`Email verification resent to ${user.email}.`) })
        .catch((e) => { window.alert("Error:", R.pathOr(e.message, ['response', 'body', 'message'], e)) });
    }
  };

  return (
    <Segment
      as={Grid}
      basic
      tertiary={user.status !== STATUS.ACTIVE}
      divided
      stackable
      celled
    >
      <Grid.Row>
        <UserMenu
          status={user.status}
          active={activePane}
          onMenuClick={handleMenuClick}
          onRefreshUser={handleRefreshUser}
          onResendVerification={handleResendVerification}
          userPanes={USER_PANES}
        />
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width="4">
          <UserInfo user={user} />
        </Grid.Column>
        <Grid.Column width="12">
          <Dimmer inverted active={!!isLoading}>
            <Loader />
          </Dimmer>
          {activePane === USER_PANES.ACCOUNT && (
            <UserProfile profile={user} onProfileSubmit={handleProfileSubmit} />
          )}
          {activePane === USER_PANES.LICENSES && (
            <UserLics
              licenses={user.licenses || []}
              setNavigation={setNavigation}
              onAddLicense={handleAddLicense}
              onRemoveLicense={handleRemoveLicense}
              onModifyLicense={handleModifyLicense}
              onReactivateLicense={handleReactivateLicense}
              onRemoveLicenseMachine={handleRemoveLicenseMachine}
              onAddSublicense={handleAddSublicense}
              onRemoveSublicense={handleRemoveSublicense}
              onDeactivateLicenseUpgrade={handleDeactivateLicenseUpgrade}
            />
          )}
          {activePane === USER_PANES.NOTES && (
            <Notes
              notes={user.notes || []}
              onAddNote={handleAddNote}
              onRemoveNote={handleRemoveNote}
            />
          )}
          {activePane === USER_PANES.ROLES && (
            <UserRoles
              profile={user}
              availableRoles={availableRoles}
              refreshUser={handleRefreshUser}
            />
          )}
          {activePane === USER_PANES.SECURITY && (
            <UserSecurity
              profile={user}
              refreshUser={handleRefreshUser}
            />
          )}
        </Grid.Column>
      </Grid.Row>
    </Segment>
  );
};

UserItem.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  setNavigation: PropTypes.func.isRequired,
  onRefreshUser: PropTypes.func.isRequired,
  onModifyUser: PropTypes.func.isRequired,
  onAddNote: PropTypes.func.isRequired,
  onRemoveNote: PropTypes.func.isRequired,
  onAddLicense: PropTypes.func.isRequired,
  onReactivateLicense: PropTypes.func.isRequired,
  onModifyLicense: PropTypes.func.isRequired,
  onRemoveLicense: PropTypes.func.isRequired,
  onAddSublicense: PropTypes.func.isRequired,
  onRemoveSublicense: PropTypes.func.isRequired,
  onRemoveLicenseMachine: PropTypes.func.isRequired,
  onDeactivateLicenseUpgrade: PropTypes.func.isRequired,
  availableRoles: PropTypes.array,
};

export default UserItem;



// WEBPACK FOOTER //
// ./webAdmin/components/users/user/index.js